/**
 * Read every page of a list endpoint that caps `limit`.
 *
 * WHY THIS EXISTS. `/categories` and `/topics` both declare
 * `limit: int = Query(100, ge=1, le=100)`. Asking for more is not clamped — it
 * is a 422, so a caller that assumes it can name its own page size gets no data
 * at all. The subject rail did exactly that and rendered an error where the
 * tree should be.
 *
 * The fix that looks obvious is to ask for 100 and move on. That is worse: a
 * subject with 140 topics would render 100 of them, in a tree, with no error
 * and nothing to indicate the other 40 exist. Silent truncation of a structure
 * is the one failure mode a tree must not have.
 *
 * So page until a short page comes back. These endpoints return a bare array
 * with no total, so a page smaller than the requested size IS the end signal —
 * there is no other one available.
 */

export interface FetchAllPagesOptions {
  /** Must not exceed the endpoint's cap, or every request 422s. */
  pageSize?: number;
  /**
   * Safety stop. An endpoint that always returns a full page — a bug, or a
   * `skip` it quietly ignores — would otherwise loop forever and hang the tab.
   * Reaching this returns what was collected rather than throwing: partial data
   * plus a visible row count beats a blank screen.
   */
  maxItems?: number;
}

export async function fetchAllPages<T>(
  fetchPage: (skip: number, limit: number) => Promise<T[]>,
  { pageSize = 100, maxItems = 5000 }: FetchAllPagesOptions = {},
): Promise<T[]> {
  const all: T[] = [];
  let skip = 0;
  for (;;) {
    const page = await fetchPage(skip, pageSize);
    all.push(...page);
    // Short page — including an empty one — means there is no more.
    if (page.length < pageSize) return all;
    if (all.length >= maxItems) return all.slice(0, maxItems);
    skip += pageSize;
  }
}

/**
 * Whether an infinite-scroll sentinel coming into view should fetch the next page.
 *
 * WHY THIS IS A FUNCTION. The obvious condition — "visible, and there is more,
 * and we are not already loading" — has a hole that only opens on failure, and
 * it is vicious when it does:
 *
 *   a fetch fails -> the list is empty -> an empty list puts the sentinel in
 *   the viewport -> the sentinel fires -> the fetch fails -> ...
 *
 * `hasMore` is no defence, because it starts `true` and a failed request never
 * got a page count to correct it. So the loop is unbounded, hammers the API,
 * and flashes the section between an error banner and an empty table several
 * times a second. That is not a hypothetical: it was reported from the Trends
 * page after a single transient failure.
 *
 * `error` therefore belongs in the condition alongside `hasMore` — they answer
 * different questions ("is there another page" vs "is the last attempt still
 * broken") and an empty list shows the sentinel either way.
 */
export function shouldFetchNextPage(state: {
  isIntersecting: boolean;
  hasMore: boolean;
  error: unknown;
  loading: boolean;
  loadingMore: boolean;
}): boolean {
  if (!state.isIntersecting) return false;
  if (!state.hasMore) return false;
  // A standing error means the last attempt failed. Retrying on scroll position
  // is not a decision the user made.
  if (state.error) return false;
  return !state.loading && !state.loadingMore;
}
